import { useState, useEffect, useCallback, useMemo } from "react";
import { Toast, TableSkeleton, Pagination, useToast } from "@hiya/ui";
import {
  listUsers,
  createUser,
  updateUser,
  disableUser,
  resendInvite,
  resetUserPassword,
} from "../../../api/users";
import type { User, UserRole } from "../../../types/user";
import { UserTable } from "../../../components/users/UserTable";
import { UserSlidePanel } from "../../../components/users/UserSlidePanel";
import { DisableUserModal } from "../../../components/users/DisableUserModal";
import { ResetPasswordModal } from "../../../components/users/ResetPasswordModal";

const PAGE_SIZE = 10;

export function UsersPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const { toast, showToast } = useToast();

  // Panel state
  const [panelMode, setPanelMode] = useState<"add" | "edit">("add");
  const [panelOpen, setPanelOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<User | null>(null);

  // Disable modal state
  const [disableTarget, setDisableTarget] = useState<User | null>(null);

  // Reset password modal state
  const [resetTarget, setResetTarget] = useState<User | null>(null);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await listUsers();
      const sorted = [...data].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      setUsers(sorted);
    } catch {
      showToast("Failed to load users", "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(users.length / PAGE_SIZE));
  const paginatedUsers = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return users.slice(start, start + PAGE_SIZE);
  }, [users, currentPage]);

  // Reset to page 1 if current page becomes out of bounds
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [currentPage, totalPages]);

  function openAddPanel() {
    setPanelMode("add");
    setEditTarget(null);
    setPanelOpen(true);
  }

  function openEditPanel(user: User) {
    setPanelMode("edit");
    setEditTarget(user);
    setPanelOpen(true);
  }

  function closePanel() {
    setPanelOpen(false);
    setEditTarget(null);
  }

  async function handleSubmit(data: {
    name: string;
    surname: string;
    role: UserRole;
    email: string;
    signature: File | null;
  }) {
    try {
      if (panelMode === "add") {
        const newUser = await createUser(
          { name: data.name, surname: data.surname, role: data.role, email: data.email },
          data.signature ?? undefined,
        );
        setUsers((prev) => [newUser, ...prev]);
        setCurrentPage(1);
        showToast("New user added");
      } else if (editTarget) {
        const updated = await updateUser(
          editTarget.id,
          { name: data.name, surname: data.surname },
          data.signature ?? undefined,
        );
        setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
        showToast("User edited");
      }
      closePanel();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Operation failed";
      showToast(message, "error");
    }
  }

  async function handleDisable() {
    if (!disableTarget) return;
    try {
      await disableUser(disableTarget.id);
      setUsers((prev) =>
        prev.map((u) => (u.id === disableTarget.id ? { ...u, status: "disabled" as const } : u)),
      );
      showToast("User disabled");
    } catch {
      showToast("Failed to disable user", "error");
    } finally {
      setDisableTarget(null);
    }
  }

  async function handleResendInvite(user: User) {
    try {
      await resendInvite(user.id);
      showToast("Invite resent");
    } catch {
      showToast("Failed to resend invite", "error");
    }
  }

  async function handleResetPassword() {
    if (!resetTarget) return;
    // Close modal optimistically: prevents double-click re-submission.
    const target = resetTarget;
    setResetTarget(null);
    try {
      await resetUserPassword(target.id);
      showToast("Password reset email sent");
    } catch {
      showToast("Failed to reset password", "error");
    }
  }

  return (
    <div>
      <Toast message={toast.message} visible={toast.visible} variant={toast.variant} />

      <div className="flex justify-end mb-5">
        <button
          type="button"
          onClick={openAddPanel}
          className="h-[36px] px-4 rounded-xl bg-btn-action text-text-btn text-sm font-normal flex items-center gap-1 whitespace-nowrap"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="shrink-0">
            <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <span>Add user</span>
        </button>
      </div>

      {loading && <TableSkeleton columns={6} />}

      {!loading && users.length === 0 && (
        <div className="text-center py-20">
          <p className="text-text-secondary text-base mb-4">No users yet</p>
          <button
            type="button"
            onClick={openAddPanel}
            className="h-[51px] px-6 rounded-2xl bg-btn-action text-text-btn text-base font-semibold"
          >
            Add user
          </button>
        </div>
      )}

      {!loading && users.length > 0 && (
        <>
          <UserTable
            users={paginatedUsers}
            onEdit={openEditPanel}
            onDisable={setDisableTarget}
            onResendInvite={handleResendInvite}
            onResetPassword={setResetTarget}
          />
          <div className="mt-5">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        </>
      )}

      <UserSlidePanel
        open={panelOpen}
        mode={panelMode}
        user={editTarget}
        onClose={closePanel}
        onSubmit={handleSubmit}
      />

      <DisableUserModal
        user={disableTarget}
        onClose={() => setDisableTarget(null)}
        onConfirm={handleDisable}
      />

      <ResetPasswordModal
        user={resetTarget}
        onClose={() => setResetTarget(null)}
        onConfirm={handleResetPassword}
      />
    </div>
  );
}
